import { FC, ReactNode, useMemo, useState } from "react";
import { Box, CssBaseline } from "@mui/material";
import NavigationDrawer, {
  drawerWidth,
} from "../navigationDrawer/navigationDrawer";
import HeaderToolbar from "../headerToolbar/headerToolbar";
import Breadcrumbs from "../breadcrumbs/breadcrumbs";
import HelpDialog from "../help/HelpDialog";
import { useLocation } from "react-router-dom";
import { pages } from "../../../utils/common";
import { BASE_TITLE, CONSTANTS } from "../../../utils/CONSTS";
import DocumentTitle from "../documentTitle/DocumentTitle";

export type MainLayoutProps = {
  children: ReactNode;
};

const MainLayout: FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(true);

  // Get current page title from location
  const getCurrentPageTitle = () => {
    const pathname = location.pathname;

    // Find matching page in pages object
    const pageKey = Object.keys(pages).find((key) => {
      const page = pages[key];
      return (
        page.path === pathname ||
        page.aliasPath === pathname ||
        pathname.startsWith(page.path + "/")
      );
    });

    if (pageKey) {
      return pages[pageKey].title;
    }

    // Fallback: capitalize first letter of path segment
    const segments = pathname.split("/").filter((x) => x);
    if (segments.length > 0) {
      return (
        segments[segments.length - 1].charAt(0).toUpperCase() +
        segments[segments.length - 1].slice(1)
      );
    }

    return "Dashboard";
  };

  // Get selected page path for navigation highlighting
  const getSelectedPage = () => {
    return location.pathname;
  };

  // Get browser tab title based on current page
  const browserTitle = useMemo(() => {
    const pathname = location.pathname;

    // Find matching page in pages object
    const pageKey = Object.keys(pages).find((key) => {
      const page = pages[key];
      return (
        page.path === pathname ||
        page.aliasPath === pathname ||
        pathname.startsWith(page.path + "/")
      );
    });

    if (pageKey && CONSTANTS.title[pageKey as keyof typeof CONSTANTS.title]) {
      return CONSTANTS.title[pageKey as keyof typeof CONSTANTS.title];
    } else {
      // Fallback: use page title with base title
      let pageTitle = "Dashboard";
      if (pageKey && pages[pageKey]) {
        pageTitle = pages[pageKey].title;
      } else {
        // Fallback: capitalize first letter of path segment
        const segments = pathname.split("/").filter((x) => x);
        if (segments.length > 0) {
          pageTitle =
            segments[segments.length - 1].charAt(0).toUpperCase() +
            segments[segments.length - 1].slice(1);
        }
      }
      return `${BASE_TITLE} ${pageTitle}`;
    }
  }, [location.pathname]);

  const handleDrawerToggle = (open: boolean) => {
    setDrawerOpen(open);
  };

  return (
    <>
      <DocumentTitle title={browserTitle} />
      <Box
        sx={{ display: "flex", height: "100vh", overflow: "hidden" }}
        data-testid="main-layout"
      >
        <CssBaseline />
        <NavigationDrawer
          selectedPage={getSelectedPage()}
          window={window}
          onDrawerToggle={handleDrawerToggle}
          drawerOpen={drawerOpen}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: drawerOpen
              ? `calc(100% - ${drawerWidth}px)`
              : `calc(100% - 64px)`,
            transition: (theme) =>
              theme.transitions.create(["width", "margin"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
          }}
          data-testid="main-content-container"
        >
          <HeaderToolbar
            pageTitle={getCurrentPageTitle()}
            drawerWidth={drawerWidth}
            drawerOpen={drawerOpen}
          />
          <Box
            sx={{
              mt: "64px", // AppBar height
              flexGrow: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
            data-testid="main-content-wrapper"
          >
            {/* <Breadcrumbs drawerWidth={drawerWidth} drawerOpen={drawerOpen} /> */}
            <Box
              sx={{
                flexGrow: 1,
                p: { xs: 2, sm: 3 },
              }}
              data-testid="main-content-area"
            >
              {children}
            </Box>
          </Box>
          <HelpDialog />
        </Box>
      </Box>
    </>
  );
};

export default MainLayout;
